import { ChangeEvent, DragEvent, useRef, useState } from 'react'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import CloseIcon from '@mui/icons-material/Close'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { DropzoneStyled, ExistingFileLinkStyled, FilePillStyled } from './onboardingStyles'

interface IFileField {
  inputId: string
  accept: string
  hint: string
  icon: 'document' | 'photo'
  file: File | null
  existingFileUrl?: string | null
  invalid?: boolean
  onSelect: (file: File | null) => void
}

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export const FileField = ({
  inputId,
  accept,
  hint,
  icon,
  file,
  existingFileUrl,
  invalid,
  onSelect
}: IFileField) => {
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onSelect(event.target.files?.[0] ?? null)
  }

  const handleRemove = () => {
    onSelect(null)
    // Clear the native input so re-selecting the same file still fires onChange.
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setDragging(false)
    onSelect(event.dataTransfer.files?.[0] ?? null)
  }

  const hasExisting = !file && !!existingFileUrl
  const primaryText = hasExisting ? 'Replace uploaded file' : 'Click to upload'

  return (
    <>
      <DropzoneStyled
        htmlFor={inputId}
        className={dragging ? 'is-drag' : undefined}
        aria-invalid={invalid || undefined}
        onDragEnter={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <span className="ico">
          {icon === 'photo' ? (
            <PersonOutlineIcon sx={{ fontSize: 22 }} />
          ) : (
            <BadgeOutlinedIcon sx={{ fontSize: 22 }} />
          )}
        </span>
        <div className="copy">
          <div className="t1">
            <b>{primaryText}</b> or drag and drop
          </div>
          <div className="t2">{hint}</div>
        </div>
        <input ref={inputRef} id={inputId} type="file" accept={accept} onChange={handleChange} />
      </DropzoneStyled>

      {file && (
        <FilePillStyled>
          <span className="name">{file.name}</span>
          <span className="size">· {formatSize(file.size)}</span>
          <button type="button" className="x" aria-label="Remove file" onClick={handleRemove}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </button>
        </FilePillStyled>
      )}

      {hasExisting && (
        <FilePillStyled>
          <ExistingFileLinkStyled
            href={existingFileUrl ?? undefined}
            target="_blank"
            rel="noreferrer"
            className="name"
          >
            View uploaded file
          </ExistingFileLinkStyled>
        </FilePillStyled>
      )}
    </>
  )
}
